import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SnippetsOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import ActionHeader from "../../../components/ActionHeader";
import BaseTable from "../../../components/BaseTable";
import usePageState from "../../../hooks/usePageState";
import { useState } from "react";
import {
  OrderItemMappingRequest,
  OrderResponse,
} from "../../../../infrastructure/model/OrderModel";
import { FilterRequest } from "../../../../applications/constants/AppTypes";
import {
  ADMINISTRATOR_ID,
  ORDER_STATUS_ENUM,
  SIZE_MAPPING_OPTION,
} from "../../../../applications/constants/AppConst";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { Button, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useItemsContext } from "../../../context/AppItemsContext";
import Row from "../../../components/Row";
import { currencyFormat } from "../../../../applications/utils/NumberUtils";
import OrderBillDialog from "./OrderBillDialog";
import { useToppingContext } from "../../../context/AppToppingContext";
import useConfirmAction from "../../../hooks/useConfirmAction";
import KitchenBillDialog from "./KitchenBillDialog";
import { useReload } from "../../../context/AppReloadContext";
import Visible from "../../../components/Visible";
import { useAuth } from "../../../context/AuthContext";
import useModal from "../../../hooks/useModal";
import { isSameDay } from "../../../../applications/utils/TimeUtils";
import ConfirmDeleteOrderDialog from "./ConfirmDeleteOrderDialog";
import ConfirmReturnOrderDialog from "./ConfirmReturnOrderDialog";
import PreviewOrderTotalDialog from "./PreviewOrderTotalDialog";
import {
  getAllOrderUseCase,
  getItemInOrderUseCase,
  updateOrderStatusAndUserInfoUseCase,
} from "../../../../applications/useCase/ApiUseCase/OrderApiUseCase";
interface AdminOrdersProps {
  status: ORDER_STATUS_ENUM;
  setMenuItemSelected?: (value: string) => void;
  setUpdateOrderId?: (value: number) => void;
}

function AdminOrders(props: AdminOrdersProps) {
  const { status, setMenuItemSelected, setUpdateOrderId } = props;

  const { isLoading, setLoading, showError, showSuccess, showClientError } = usePageState();
  const { reloadTrigger, setReloadTrigger } = useReload();
  const { items } = useItemsContext();
  const { toppings } = useToppingContext();
  const [selectedItem, setSelectedItem] = useState<OrderResponse>();
  const [dataTable, setDataTable] = useState<OrderResponse[]>([]);
  const [openOrderBill, setOpenOrderBill] = useState<boolean>(false);
  const [openPreviewHandle, setOpenPreviewHandle] = useState<number>();
  const [openKitchenBill, setOpenKitchenBill] = useState<boolean>(false);
  const { authData } = useAuth();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [deleteOrderDialog, openDeleteOrderDialog] = useModal();
  const [returnOrderDialog, openReturnOrderDialog] = useModal();
  const { dialog: confirmOrderDialog, confirm: handleConfirm } = useConfirmAction({
    onConfirm: async (params: any) => await onUpdateOrderStatus(params?.orderId, params?.newStatus),
    message: "Bạn có chắc chắn muốn cập nhật trạng thái của đơn hàng này không?",
    header: "Cập nhật trạng thái đơn hàng",
  });

  const [total, setTotal] = useState<number>(0);
  const [filter, setFilter] = useState<FilterRequest & { status: number }>({
    page: 1,
    limit: 30,
    status: status || ORDER_STATUS_ENUM.NOT_CONFIRM,
  });

  async function handleDeleteOrder(orderId: number) {
    const result = await openDeleteOrderDialog((close) => (
      <ConfirmDeleteOrderDialog onClose={close} orderId={orderId} />
    ));
    if (result) {
      setDataTable(dataTable.filter((d) => d.id !== orderId));
    }
  }

  async function handleReturnOrder(orderId: number) {
    const result = await openReturnOrderDialog((close) => (
      <ConfirmReturnOrderDialog onClose={close} orderId={orderId} />
    ));
    if (result) {
      setDataTable(dataTable.filter((d) => d.id !== orderId));
    }
  }

  async function getAllOrders() {
    setExpandedRowKeys([]);
    setDataTable([]);
    setSelectedItem(undefined);
    const res = await getAllOrderUseCase({
      page: filter.page,
      limit: filter.limit,
      status: [status],
      showError,
      setLoading,
    });
    if (res) {
      setDataTable(res.data);
      setTotal(res.total);
    }
  }

  async function onUpdateOrderStatus(orderId: number, newStatus: ORDER_STATUS_ENUM) {
    const res = await updateOrderStatusAndUserInfoUseCase({
      orderId,
      newStatus,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setDataTable(dataTable.filter((d) => d.id !== orderId));
    }
  }

  async function getItemsInOrder(orderId: number) {
    const indexUpdate = dataTable.findIndex((e) => e.id === orderId);
    if (dataTable?.[indexUpdate]?.items?.length > 0) return;
    const res = await getItemInOrderUseCase({
      orderId,
      setLoading,
      showError,
      showSuccess,
    });
    if (res) {
      setDataTable([
        ...dataTable.slice(0, indexUpdate),
        {
          ...dataTable[indexUpdate],
          items: res,
        },
        ...dataTable.slice(indexUpdate + 1),
      ]);
    }
  }

  useAsyncEffect(async () => {
    await getAllOrders();
  }, [filter]);

  useAsyncEffect(async () => {
    if (reloadTrigger) {
      await getAllOrders();
      setReloadTrigger(false);
    }
  }, [reloadTrigger]);

  const expandedRowRender = (record: OrderResponse) => {
    if (!record.items || record.items.length === 0) return null;
    const nestedColumns: ColumnsType<OrderItemMappingRequest> = [
      {
        title: "ID",
        dataIndex: "item_id",
        key: "item_id",
        width: 50,
      },
      {
        title: "Tên sản phẩm",
        dataIndex: "item_name",
        key: "item_name",
        width: 250,
        render(value, record, index) {
          return items.find((e) => e.id === record.item_id)?.name;
        },
      },
      {
        title: "Số lượng",
        dataIndex: "item_quantity",
        key: "item_quantity",
        width: 80,
      },
      {
        title: "Topping gọi thêm",
        dataIndex: "topping_ids",
        key: "topping_ids",
        width: 250,
        render(value, record, index) {
          return toppings
            ?.filter((t) => value?.includes(t.id))
            ?.map((t) => t.name)
            ?.join(" ,");
        },
      },
      {
        title: "Ghi chú",
        dataIndex: "item_note",
        key: "item_note",
        width: 250,
      },
      {
        title: "Size",
        dataIndex: "item_size",
        key: "item_size",
        width: 100,
        render(value, record, index) {
          return SIZE_MAPPING_OPTION.find((s) => s.value === value)?.label;
        },
      },
      {
        title: "Giá tiền",
        dataIndex: "item_price",
        key: "item_price",
        width: 200,
        render(value, record, index) {
          const totalToppingPrice =
            toppings
              ?.filter((t) => record.topping_ids?.includes(t.id) && t.can_sale)
              ?.reduce((sum, t) => sum + t.price, 0) || 0;
          const itemPrice = value;
          const total = itemPrice + totalToppingPrice;
          return currencyFormat(total);
        },
      },
    ];
    return (
      <Table
        scroll={{
          y: "300px",
        }}
        columns={nestedColumns}
        dataSource={record.items}
        pagination={false}
        rowKey="item_id"
      />
    );
  };

  const handleValidateCanUpdateOrder = () => {
    if (!selectedItem?.id) {
      showClientError("Không tìm thấy đơn hàng cần chỉnh sửa");
      return;
    }
    const orderCreatedDate = selectedItem.created_at;
    const validSameDay = isSameDay(orderCreatedDate);
    if (!validSameDay) {
      showClientError("Không thể chỉnh sửa đơn hàng này, đã quá ngày cho phép");
      return;
    }
    setMenuItemSelected?.("order");
    setUpdateOrderId?.(selectedItem.id);
  };

  return (
    <div className="h-100 flex flex-column">
      <ActionHeader
        rowCount={total}
        actions={[
          {
            icon: <ReloadOutlined />,
            isNormal: true,
            userClick: getAllOrders,
            type: "primary",
          },
          {
            icon: <EditOutlined />,
            isNormal: true,
            userClick: () => {
              handleValidateCanUpdateOrder();
            },
            type: "primary",
            variant: "solid",
            color: "orange",
            hidden: authData?.id !== ADMINISTRATOR_ID,
            disabled: !selectedItem?.id,
          },
        ]}
      />
      <BaseTable
        dataSource={dataTable}
        loading={isLoading}
        columns={[
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 100,
          },
          {
            title: "Mã đơn hàng",
            dataIndex: "code",
            key: "code",
          },
          {
            title: "Hành động",
            dataIndex: "",
            key: "x",
            render: (value, record, index) => (
              <>
                <Row className="gap-2">
                  <Visible
                    isVisible={[ORDER_STATUS_ENUM.APPROVE, ORDER_STATUS_ENUM.COMPLETE].includes(
                      status
                    )}
                  >
                    <Tooltip title="Xem tổng hợp" placement="top">
                      <Button
                        type="default"
                        variant="solid"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setOpenPreviewHandle(record.id);
                        }}
                      />
                    </Tooltip>
                  </Visible>
                  <Visible
                    isVisible={[ORDER_STATUS_ENUM.APPROVE, ORDER_STATUS_ENUM.COMPLETE].includes(
                      status
                    )}
                  >
                    <Tooltip title="In hóa đơn" placement="top">
                      <Button
                        type="text"
                        color="geekblue"
                        variant="solid"
                        icon={<PrinterOutlined />}
                        onClick={() => {
                          setOpenOrderBill(true);
                          setSelectedItem(record);
                        }}
                      />
                    </Tooltip>
                  </Visible>
                  <Visible isVisible={[ORDER_STATUS_ENUM.COMPLETE].includes(status)}>
                    <Tooltip title="In phiếu cho pha chế" placement="top">
                      <Button
                        type="text"
                        color="geekblue"
                        variant="solid"
                        icon={<SnippetsOutlined />}
                        onClick={() => {
                          setOpenKitchenBill(true);
                          setSelectedItem(record);
                        }}
                      />
                    </Tooltip>
                  </Visible>
                  <Visible isVisible={status === ORDER_STATUS_ENUM.NOT_CONFIRM}>
                    <Tooltip title="Xác nhận đơn" placement="top">
                      <Button
                        type="text"
                        color="cyan"
                        variant="solid"
                        icon={<CheckCircleOutlined />}
                        onClick={() =>
                          handleConfirm({
                            orderId: record.id,
                            newStatus: ORDER_STATUS_ENUM.APPROVE,
                          })
                        }
                      />
                    </Tooltip>
                  </Visible>
                  <Visible
                    isVisible={
                      ![
                        ORDER_STATUS_ENUM.COMPLETE,
                        ORDER_STATUS_ENUM.RETURN,
                        ORDER_STATUS_ENUM.REJECT,
                        ORDER_STATUS_ENUM.DELETE,
                      ]?.includes(status)
                    }
                  >
                    <Tooltip title="Hủy" placement="top">
                      <Button
                        type="text"
                        color="orange"
                        variant="solid"
                        icon={<CloseCircleOutlined />}
                        onClick={() =>
                          handleConfirm({
                            orderId: record.id,
                            newStatus: ORDER_STATUS_ENUM.REJECT,
                          })
                        }
                      />
                    </Tooltip>
                  </Visible>
                  <Visible
                    isVisible={
                      [ORDER_STATUS_ENUM.APPROVE, ORDER_STATUS_ENUM.NOT_CONFIRM]?.includes(
                        status
                      ) ||
                      (ORDER_STATUS_ENUM.COMPLETE === status && authData?.id === ADMINISTRATOR_ID)
                    }
                  >
                    <Tooltip title="Xóa" placement="top">
                      <Button
                        type="text"
                        color="danger"
                        variant="solid"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteOrder(record.id)}
                      />
                    </Tooltip>
                  </Visible>
                  <Visible isVisible={status === ORDER_STATUS_ENUM.COMPLETE}>
                    <Tooltip title="Hoàn hàng" placement="top">
                      <Button
                        type="text"
                        color="danger"
                        variant="solid"
                        icon={<UndoOutlined />}
                        onClick={() => handleReturnOrder(record.id)}
                      />
                    </Tooltip>
                  </Visible>
                  <Visible
                    isVisible={
                      ![
                        ORDER_STATUS_ENUM.RETURN,
                        ORDER_STATUS_ENUM.REJECT,
                        ORDER_STATUS_ENUM.DELETE,
                      ]?.includes(status)
                    }
                  >
                    <Tooltip title="Copy" placement="top">
                      <Button
                        type="primary"
                        color="geekblue"
                        variant="solid"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          setMenuItemSelected?.("order");
                          setUpdateOrderId?.(record.id);
                        }}
                      />
                    </Tooltip>
                  </Visible>
                </Row>
              </>
            ),
          },
          {
            title: "Nguời đặt hàng",
            dataIndex: "username",
            key: "getAllOrders",
            width: 200,
          },
          {
            title: "Số điện thoại",
            dataIndex: "user_phone",
            key: "user_phone",
            width: 100,
          },
          {
            title: "Địa chỉ giao hàng",
            dataIndex: "address",
            key: "address",
            minWidth: 100,
          },
          {
            title: "Ghi chú",
            dataIndex: "order_note",
            key: "order_note",
            minWidth: 100,
            width: 200,
          },
          {
            title: "Giá tiền gốc",
            dataIndex: "original_price",
            key: "original_price",
            minWidth: 100,
            render(value, record, index) {
              return currencyFormat(value);
            },
          },

          {
            title: "Giá bán",
            dataIndex: "discount_price",
            key: "discount_price",
            minWidth: 100,
            render(value, record, index) {
              return currencyFormat(value - record.counter_discount);
            },
          },
          {
            title: "Giảm giá tại quầy",
            dataIndex: "counter_discount",
            key: "counter_discount",
            minWidth: 100,
            render(value, record, index) {
              return currencyFormat(value);
            },
          },
          {
            title: "Áp dụng KM",
            dataIndex: "apply_vouchers",
            key: "apply_vouchers",
            width: 300,
            minWidth: 200,
          },

          {
            title: "Trạng thái",
            dataIndex: "order_status",
            key: "order_status",
            width: 100,
            render(value, record, index) {
              return (
                <Tag
                  color={
                    value === ORDER_STATUS_ENUM.NOT_CONFIRM
                      ? "warning"
                      : value === ORDER_STATUS_ENUM.APPROVE
                      ? "processing"
                      : value === ORDER_STATUS_ENUM.COMPLETE
                      ? "success"
                      : "error"
                  }
                >
                  {ORDER_STATUS_ENUM?.[value]}
                </Tag>
              );
            },
          },

          {
            title: "Thời gian tạo",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render(value, record, index) {
              return new Date(value).toLocaleString("vi-VN", {
                minute: "2-digit",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
              });
            },
          },
          {
            title: "Thời gian cập nhật",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render(value, record, index) {
              return new Date(value).toLocaleString("vi-VN", {
                minute: "2-digit",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
              });
            },
          },
          {
            title: "Người cập nhật",
            dataIndex: "updated_by",
            key: "updated_by",
          },
        ]}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              // Thêm key nếu chưa có
              setExpandedRowKeys((prev) => [...prev, record.id]);
              getItemsInOrder(record.id); // Gọi dữ liệu khi mở
            } else {
              // Loại bỏ key khi đóng
              setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
            }
          },
        }}
        rowKey={"id"}
        rowSelection={{
          type: "radio",
          selectedRowKeys: selectedItem?.id ? [selectedItem.id] : [],
          onSelect(record) {
            setSelectedItem(record);
          },
        }}
        pagination={{
          total: total,
          pageSize: filter.limit,
          onChange(page, pageSize) {
            setFilter({ ...filter, page: page, limit: pageSize });
          },
        }}
      />
      {openOrderBill && selectedItem && (
        <OrderBillDialog
          onClose={(e) => {
            setOpenOrderBill(false);
            if (filter.status !== ORDER_STATUS_ENUM.COMPLETE && e) {
              setDataTable(dataTable?.filter((d) => d.id !== selectedItem!.id));
            }

            setSelectedItem(undefined);
          }}
          orderItem={selectedItem!}
          isTakeAway={selectedItem.take_away}
        />
      )}
      {openKitchenBill && selectedItem && (
        <KitchenBillDialog onClose={setOpenKitchenBill} orderItem={selectedItem!} />
      )}
      {confirmOrderDialog}
      {deleteOrderDialog}
      {returnOrderDialog}
      {openPreviewHandle && (
        <PreviewOrderTotalDialog
          openPreviewHandle={openPreviewHandle}
          setOpenPreviewHandle={setOpenPreviewHandle}
        />
      )}
    </div>
  );
}
export default AdminOrders;
